import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCenter } from "@/context/CenterContext";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Copy, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import PositionsManager, { POSITION_COLORS } from "@/components/PositionsManager";
import CopyScheduleDialog from "@/components/CopyScheduleDialog";
import {
  startOfWeek, addDays, format, addWeeks, isSameDay, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth,
} from "date-fns";

const COLORS = ["#3B82F6", "#8B5CF6", "#F59E0B", "#10B981", "#EF4444", "#EC4899", "#06B6D4"];

function fmt(d) { return format(d, "yyyy-MM-dd"); }
function nameParts(n) { const p = (n || "").trim().split(/\s+/); return { first: p[0] || "", last: p.length > 1 ? p[p.length - 1] : "" }; }
function sortEmployees(list, sortBy) {
  const cmp = (a, b) => a.localeCompare(b, "fr", { sensitivity: "base" });
  const arr = [...list];
  arr.sort((a, b) => {
    switch (sortBy) {
      case "prenom_za": return cmp(nameParts(b.name).first, nameParts(a.name).first);
      case "nom_az": return cmp(nameParts(a.name).last, nameParts(b.name).last);
      case "nom_za": return cmp(nameParts(b.name).last, nameParts(a.name).last);
      default: return cmp(nameParts(a.name).first, nameParts(b.name).first);
    }
  });
  return arr;
}
function hoursBetween(s, e) {
  const [sh, sm] = s.split(":").map(Number); const [eh, em] = e.split(":").map(Number);
  let diff = (eh * 60 + em - sh * 60 - sm) / 60; if (diff <= 0) diff += 24; return Math.round(diff * 10) / 10;
}

function ShiftDialog({ open, onOpenChange, shift, employees, positions, onPositionsChange, defaults, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({});
  const [addingPos, setAddingPos] = useState(false);
  const [newPos, setNewPos] = useState({ name: "", color: POSITION_COLORS[0] });
  useEffect(() => {
    if (open) { setForm(shift || { employee_id: defaults?.employee_id || "", date: defaults?.date || fmt(new Date()), start_time: "08:00", end_time: "16:00", role: "", color: "#3B82F6", position_id: null, notes: "", is_open: !defaults?.employee_id }); setAddingPos(false); }
  }, [open, shift, defaults]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const eligible = employees.filter((e) => !form.position_id || (e.position_ids || []).includes(form.position_id));

  const pickPosition = (v) => {
    if (v === "none") { set("position_id", null); return; }
    const p = positions.find((x) => x.id === v);
    setForm((f) => {
      const next = { ...f, position_id: v, color: p ? p.color : f.color };
      if (f.employee_id && !(employees.find((e) => e.id === f.employee_id)?.position_ids || []).includes(v)) next.employee_id = "";
      return next;
    });
  };
  const createPos = async () => {
    if (!newPos.name.trim()) return;
    try {
      const { data } = await api.post("/positions", { ...newPos, active: true });
      await onPositionsChange();
      set("position_id", data.id); set("color", data.color);
      setAddingPos(false); setNewPos({ name: "", color: POSITION_COLORS[0] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.is_open) payload.employee_id = null;
      if (shift) await api.put(`/schedule/shifts/${shift.id}`, payload);
      else await api.post("/schedule/shifts", payload);
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => { await api.delete(`/schedule/shifts/${shift.id}`); toast.success(t("delete")); onSaved(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{shift ? t("editShift") : t("addShift")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("requiredPosition")}</Label>
            <Select value={form.position_id || "none"} onValueChange={pickPosition}>
              <SelectTrigger data-testid="shift-position"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("noPositionFilter")}</SelectItem>
                {positions.filter((p) => p.active).map((p) => (
                  <SelectItem key={p.id} value={p.id}><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />{p.name}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!addingPos
              ? <button className="text-xs text-primary mt-1 inline-flex items-center gap-1" onClick={() => setAddingPos(true)} data-testid="shift-add-position"><Plus className="w-3 h-3" />{t("addPosition")}</button>
              : (
                <div className="mt-2 p-2 rounded-lg border border-border space-y-2 bg-muted/30">
                  <Input value={newPos.name} onChange={(e) => setNewPos((n) => ({ ...n, name: e.target.value }))} placeholder={t("positionName")} data-testid="inline-position-name" />
                  <div className="flex flex-wrap gap-1.5">{POSITION_COLORS.map((c) => <button key={c} onClick={() => setNewPos((n) => ({ ...n, color: c }))} style={{ background: c }} className={`w-6 h-6 rounded-full ${newPos.color === c ? "ring-2 ring-offset-1 ring-foreground" : ""}`} />)}</div>
                  <div className="flex gap-2 justify-end"><Button size="sm" variant="outline" onClick={() => setAddingPos(false)}>{t("cancel")}</Button><Button size="sm" onClick={createPos} data-testid="inline-position-save">{t("save")}</Button></div>
                </div>
              )}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="isopen" checked={!!form.is_open} onChange={(e) => set("is_open", e.target.checked)} className="w-4 h-4" data-testid="shift-open" />
            <Label htmlFor="isopen">{t("openShift")}</Label>
          </div>
          {!form.is_open && (
            <div className="space-y-1.5"><Label className="text-xs">{t("assignTo")}</Label>
              <Select value={form.employee_id || ""} onValueChange={(v) => set("employee_id", v)}>
                <SelectTrigger data-testid="shift-employee"><SelectValue placeholder={t("assignTo")} /></SelectTrigger>
                <SelectContent>{eligible.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
              {form.position_id && eligible.length === 0 && <p className="text-xs text-amber-600">{t("noneEligible")}</p>}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5"><Label className="text-xs">{t("day")}</Label><Input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="shift-date" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("startTime")}</Label><Input type="time" value={form.start_time || ""} onChange={(e) => set("start_time", e.target.value)} data-testid="shift-start" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("endTime")}</Label><Input type="time" value={form.end_time || ""} onChange={(e) => set("end_time", e.target.value)} data-testid="shift-end" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("notes")}</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter className="flex-row justify-between">
          {shift ? <Button variant="ghost" onClick={del} data-testid="delete-shift"><Trash2 className="w-4 h-4 text-destructive" /></Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-shift">{t("save")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShiftChip({ s, onClick, draggable, onDragStart, posMap }) {
  const pos = posMap && s.position_id ? posMap[s.position_id] : null;
  const label = pos?.name || s.role;
  return (
    <div onClick={onClick} draggable={draggable} onDragStart={onDragStart} data-testid="shift-chip"
      className="rounded-md px-2 py-1 text-xs text-white cursor-pointer mb-1 hover:opacity-90 transition-opacity"
      style={{ backgroundColor: pos?.color || s.color || "#3B82F6" }}>
      <div className="font-semibold">{s.start_time}–{s.end_time}</div>
      {label && <div className="opacity-90 truncate">{label}</div>}
    </div>
  );
}

export default function Schedule({ employeeMode = false }) {
  const { t, lang } = useT();
  const { user } = useAuth();
  const { isManager } = useCenter();
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialog, setDialog] = useState({ open: false, shift: null, defaults: null });
  const [sortBy, setSortBy] = useState("prenom_az");
  const [positions, setPositions] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const posMap = Object.fromEntries(positions.map((p) => [p.id, p]));
  const loadPositions = useCallback(() => api.get("/positions").then((r) => setPositions(r.data)).catch(() => {}), []);

  const weekStart = startOfWeek(anchor, { weekStartsOn: 1 });
  const days = view === "month"
    ? eachDayOfInterval({ start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }), end: endOfMonth(anchor) })
    : Array.from({ length: view === "day" ? 1 : 7 }, (_, i) => addDays(view === "day" ? anchor : weekStart, i));

  const load = useCallback(async () => {
    const start = fmt(view === "month" ? startOfMonth(anchor) : (view === "day" ? anchor : weekStart));
    const end = fmt(view === "month" ? endOfMonth(anchor) : (view === "day" ? anchor : addDays(weekStart, 6)));
    const params = { start, end };
    if (employeeMode) params.employee_id = user.id;
    const [sh, emp] = await Promise.all([
      api.get("/schedule/shifts", { params }),
      isManager ? api.get("/employees") : Promise.resolve({ data: [] }),
    ]);
    let list = sh.data;
    if (employeeMode) {
      const open = await api.get("/schedule/shifts", { params: { start, end } });
      list = [...sh.data, ...open.data.filter((x) => x.is_open)];
    }
    setShifts(list);
    setEmployees(emp.data.filter((e) => e.active));
  }, [anchor, view, employeeMode, isManager, user.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPositions(); }, [loadPositions]);

  const rows = employeeMode ? [{ id: user.id, name: user.name, profile_picture: user.profile_picture, self: true }] : sortEmployees(employees, sortBy);

  const move = async (shiftId, employeeId, date) => {
    try { await api.put(`/schedule/shifts/${shiftId}`, { employee_id: employeeId, date, is_open: !employeeId }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const claim = async (id) => { try { await api.post(`/schedule/shifts/${id}/claim`); toast.success("✓"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const shiftsFor = (empId, day) => shifts.filter((s) => s.employee_id === empId && isSameDay(parseISO(s.date), day));
  const openShiftsFor = (day) => shifts.filter((s) => s.is_open && isSameDay(parseISO(s.date), day));

  const r1 = (n) => Math.round(n * 10) / 10;
  const empWeekTotal = (empId) => r1(days.reduce((a, day) => a + shiftsFor(empId, day).reduce((b, s) => b + hoursBetween(s.start_time, s.end_time), 0), 0));
  const openTotalFor = (day) => openShiftsFor(day).reduce((b, s) => b + hoursBetween(s.start_time, s.end_time), 0);
  const openWeekTotal = r1(days.reduce((a, day) => a + openTotalFor(day), 0));
  const dayGrand = (day) => r1(rows.reduce((a, emp) => a + shiftsFor(emp.id, day).reduce((b, s) => b + hoursBetween(s.start_time, s.end_time), 0), 0) + (employeeMode ? 0 : openTotalFor(day)));
  const grandTotal = r1(days.reduce((a, day) => a + dayGrand(day), 0));

  const nav = (dir) => setAnchor(view === "month" ? addWeeks(anchor, dir * 4) : addWeeks(anchor, dir));

  const title = view === "month"
    ? format(anchor, "MMMM yyyy")
    : view === "day" ? format(anchor, "EEEE, MMM d") : `${format(weekStart, "MMM d")} – ${format(addDays(weekStart, 6), "MMM d")}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{employeeMode ? t("mySchedule") : t("schedule")}</h1>
          <p className="text-muted-foreground mt-1 capitalize">{title}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {["day", "week", "month"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors ${view === v ? "bg-primary text-white" : "hover:bg-accent"}`}>{t(v)}</button>
            ))}
          </div>
          <Button variant="outline" size="icon" onClick={() => nav(-1)} data-testid="nav-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="nav-today">{t("today")}</Button>
          <Button variant="outline" size="icon" onClick={() => nav(1)} data-testid="nav-next"><ChevronRight className="w-4 h-4" /></Button>
          {isManager && !employeeMode && view !== "month" && (
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-44" data-testid="schedule-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prenom_az">{t("sortPrenomAz")}</SelectItem>
                <SelectItem value="prenom_za">{t("sortPrenomZa")}</SelectItem>
                <SelectItem value="nom_az">{t("sortNomAz")}</SelectItem>
                <SelectItem value="nom_za">{t("sortNomZa")}</SelectItem>
              </SelectContent>
            </Select>
          )}
          {isManager && !employeeMode && view !== "month" && <Button variant="outline" size="sm" onClick={() => setCopyOpen(true)} data-testid="copy-week"><Copy className="w-4 h-4 mr-1.5" />{t("copyWeek")}</Button>}
          {isManager && !employeeMode && <Button size="sm" onClick={() => setDialog({ open: true, shift: null, defaults: { date: fmt(anchor) } })} data-testid="add-shift-button"><Plus className="w-4 h-4 mr-1" />{t("addShift")}</Button>}
        </div>
      </div>

      {/* Open shifts banner for employee */}
      {employeeMode && shifts.some((s) => s.is_open) && (
        <div className="mb-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10">
          <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Clock className="w-4 h-4" />{t("openShifts")}</p>
          <div className="flex flex-wrap gap-2">
            {shifts.filter((s) => s.is_open).map((s) => (
              <div key={s.id} className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-1.5 text-sm">
                <span>{format(parseISO(s.date), "MMM d")} · {s.start_time}–{s.end_time}</span>
                <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => claim(s.id)} data-testid="claim-shift">{t("claimShift")}</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "month" ? (
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border border-border">
          {[t("monday"), t("tuesday"), t("wednesday"), t("thursday"), t("friday"), t("saturday"), t("sunday")].map((d) => (
            <div key={d} className="bg-muted p-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.slice(0, 3)}</div>
          ))}
          {days.map((day) => {
            const dayShifts = shifts.filter((s) => isSameDay(parseISO(s.date), day));
            return (
              <div key={day.toISOString()} className={`bg-card min-h-24 p-1.5 ${!isSameMonth(day, anchor) ? "opacity-40" : ""}`}
                onClick={() => isManager && !employeeMode && setDialog({ open: true, shift: null, defaults: { date: fmt(day) } })} data-testid="month-cell">
                <div className="text-xs font-medium mb-1">{format(day, "d")}</div>
                {dayShifts.slice(0, 3).map((s) => <div key={s.id} className="text-[10px] rounded px-1 mb-0.5 text-white truncate" style={{ background: s.color }} onClick={(e) => { e.stopPropagation(); setDialog({ open: true, shift: s }); }}>{s.start_time} {s.is_open ? "OPEN" : ""}</div>)}
                {dayShifts.length > 3 && <div className="text-[10px] text-muted-foreground">+{dayShifts.length - 3}</div>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin border border-border rounded-lg">
          <div className="min-w-[720px]">
            <div className="grid" style={{ gridTemplateColumns: `180px repeat(${days.length}, 1fr) 84px` }}>
              <div className="bg-muted p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">{t("employees")}</div>
              {days.map((day) => (
                <div key={day.toISOString()} className={`bg-muted p-3 text-center border-b border-l border-border ${isSameDay(day, new Date()) ? "bg-primary/10" : ""}`}>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{format(day, "EEE")}</div>
                  <div className="font-display font-bold">{format(day, "d")}</div>
                </div>
              ))}
              <div className="bg-muted p-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-l border-border flex items-center justify-center">{t("total")}</div>
              {/* Open shifts row (manager) */}
              {!employeeMode && (
                <>
                  <div className="p-3 border-b border-border flex items-center gap-2 bg-amber-500/5">
                    <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center"><Clock className="w-4 h-4 text-amber-600" /></div>
                    <span className="text-sm font-medium">{t("openShifts")}</span>
                  </div>
                  {days.map((day) => (
                    <div key={day.toISOString()} className="p-2 border-b border-l border-border min-h-16 bg-amber-500/5"
                      onDragOver={(e) => e.preventDefault()} onDrop={(e) => move(e.dataTransfer.getData("id"), null, fmt(day))}>
                      {openShiftsFor(day).map((s) => <ShiftChip key={s.id} s={s} posMap={posMap} draggable onDragStart={(e) => e.dataTransfer.setData("id", s.id)} onClick={() => isManager && setDialog({ open: true, shift: s })} />)}
                    </div>
                  ))}
                  <div className="p-3 border-b border-l border-border bg-amber-500/5 flex items-center justify-end text-sm font-semibold text-amber-700">{openWeekTotal}h</div>
                </>
              )}
              {rows.map((emp) => (
                <React.Fragment key={emp.id}>
                  <div className="p-3 border-b border-border flex items-center gap-2">
                    <UserAvatar name={emp.name} src={emp.profile_picture} size={34} />
                    <span className="text-sm font-medium truncate">{emp.name}</span>
                  </div>
                  {days.map((day) => {
                    const cell = shiftsFor(emp.id, day);
                    const total = cell.reduce((a, s) => a + hoursBetween(s.start_time, s.end_time), 0);
                    return (
                      <div key={day.toISOString()} className="p-2 border-b border-l border-border min-h-16 hover:bg-accent/40 transition-colors"
                        onDragOver={(e) => isManager && e.preventDefault()}
                        onDrop={(e) => isManager && move(e.dataTransfer.getData("id"), emp.id, fmt(day))}
                        onClick={() => isManager && !employeeMode && cell.length === 0 && setDialog({ open: true, shift: null, defaults: { employee_id: emp.id, date: fmt(day) } })}
                        data-testid="schedule-cell">
                        {cell.map((s) => <ShiftChip key={s.id} s={s} posMap={posMap} draggable={isManager && !employeeMode} onDragStart={(e) => e.dataTransfer.setData("id", s.id)} onClick={(e) => { e.stopPropagation(); isManager && !employeeMode && setDialog({ open: true, shift: s }); }} />)}
                        {total > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">{r1(total)}{t("hours").slice(0, 1)}</div>}
                      </div>
                    );
                  })}
                  <div className="p-3 border-b border-l border-border flex items-center justify-end text-sm font-bold" data-testid="employee-week-total">{empWeekTotal(emp.id)}h</div>
                </React.Fragment>
              ))}
              {/* Branch total row */}
              <div className="p-3 border-t-2 border-border bg-muted/60 flex items-center text-sm font-bold">{t("branchTotal")}</div>
              {days.map((day) => (
                <div key={day.toISOString()} className="p-3 border-l border-t-2 border-border bg-muted/60 text-center text-sm font-semibold">{dayGrand(day)}h</div>
              ))}
              <div className="p-3 border-l border-t-2 border-border bg-muted/60 flex items-center justify-end text-sm font-bold text-primary" data-testid="branch-total">{grandTotal}h</div>
            </div>
          </div>
        </div>
      )}

      <ShiftDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} shift={dialog.shift} defaults={dialog.defaults} employees={employees} positions={positions} onPositionsChange={loadPositions} onSaved={load} />
      <CopyScheduleDialog open={copyOpen} onOpenChange={setCopyOpen} initialWeekStart={weekStart} onDone={load} />
    </div>
  );
}
